import { Fragment, ReactNode, useRef, useState } from 'react'

import {
  useSuggestDeliveryGetApi,
  useSuggestDeliveryUpdateApi,
  useSuggestEmbedTagGetApi,
  useSuggestManualKeywordCreateApi,
  useSuggestManualKeywordDeleteApi,
  useSuggestManualKeywordsGetApi,
  useSuggestPopularKeywordsGetApi,
  useSuggestPreviewGetApi,
  useSuggestSettingsGetApi,
  useSuggestSettingsUpdateApi,
  useSuggestWidgetKeyUpdateApi,
  useSuggestWidgetKeysGetApi
} from '@/common/api/modules/searchSuggest'
import { useSiteGetApi } from '@/common/api/modules/site'
import {
  SuggestDelivery,
  SuggestDeviceDesign,
  SuggestSettings
} from '@/common/api/params/searchSuggest'
import { useSiteId } from '@/common/app/site'
import { useShowSavedToast } from '@/common/app/toast'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import CheckBox, { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import Heading from '@/components/view/atoms/heading/Heading'
import Selector from '@/components/view/atoms/selector/Selector'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import DetailPageLoader from '@/components/container/DetailPageLoader'

const textInput =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2'

const PAGE_TABS = [
  { key: 'design', label: '設定・デザイン' },
  { key: 'popular', label: '人気キーワード' },
  { key: 'language', label: '言語・パーソナライズ' },
  { key: 'tag', label: 'タグ発行' },
  { key: 'publish', label: '公開管理' }
] as const
type PageTabKey = (typeof PAGE_TABS)[number]['key']

// 件数プルダウンの選択肢（1〜max、表記は「○件」）。0件はトグルOFFで表現するため含めない
const countChoices = (max: number) =>
  Array.from({ length: max }, (_, i) => ({ value: i + 1, name: `${i + 1}件` }))

const SettingRow = ({
  title,
  sub,
  children
}: {
  title: string
  sub?: string
  children: ReactNode
}) => (
  <div className='border-border-input flex items-center justify-between gap-4 border-t py-3 first:border-t-0'>
    <div className='min-w-0'>
      <div>
        <Text weight='bold' size={14}>
          {title}
        </Text>
      </div>
      {sub && (
        <div>
          <Text size={12} color={TextColor.SECONDARY}>
            {sub}
          </Text>
        </div>
      )}
    </div>
    <div className='shrink-0'>{children}</div>
  </div>
)

const trunc = (s: string | null | undefined, n: number): string => {
  const v = s ?? ''
  return n > 0 && v.length > n ? v.slice(0, n) + '…' : v
}

// プレビュー用CSS（埋め込みJS w2suggest.js と同じ .w2sg- クラス。運用者CSSは .w2sg-* を対象に書く）
const PREVIEW_BASE_CSS = `
.w2sg-box{position:relative;background:#fff;border:1px solid #e0e1da;border-radius:12px;box-shadow:0 8px 24px rgba(25,27,24,.1);overflow:hidden;font-size:14px;color:#191b18}
.w2sg-dym{display:flex;align-items:center;flex-wrap:wrap;gap:4px 8px;padding:10px 14px;background:#fff8ea;border-bottom:1px solid #ecece5;font-size:13px;line-height:1.4}
.w2sg-dym b{color:#c0843a;word-break:break-word;overflow-wrap:anywhere}
.w2sg-sec{padding:8px 0}.w2sg-sec+.w2sg-sec{border-top:1px solid #ecece5}
.w2sg-head{padding:3px 14px 6px;font-size:10px;letter-spacing:.1em;color:#8a8d85}
.w2sg-item{display:flex;align-items:center;gap:11px;padding:8px 14px;cursor:pointer}
.w2sg-item:hover{background:#f7f8f4}
.w2sg-w,.w2sg-a{font-size:14px}
.w2sg-thumb{width:44px;height:44px;border-radius:8px;object-fit:cover;flex-shrink:0;background:#f0f0ec}
.w2sg-meta{flex:1;min-width:0}
.w2sg-name{font-size:13.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.w2sg-catch{font-size:11.5px;color:#5c5f58;margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.w2sg-outline{font-size:11px;color:#8a8d85;margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.w2sg-psub{display:flex;align-items:center;gap:8px;margin-top:2px}
.w2sg-price{font-size:13px;font-weight:600;color:#0e8e80}
.w2sg-stock{font-size:10px;color:#0e8e80;border:1px solid #e0e1da;border-radius:5px;padding:0 5px}
.w2sg-acat{font-size:10px;color:#8a8d85}
.w2sg-topics{font-size:11px;color:#5c5f58;margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.w2sg-adate{font-size:10px;color:#8a8d85;margin-top:1px}
.w2sg-empty{padding:22px 14px;text-align:center;color:#8a8d85;font-size:13px}
`

interface PreviewResult {
  query: string
  did_you_mean: string | null
  words: { term: string; pinned?: boolean; url?: string | null }[]
  products: {
    name: string
    catchcopy?: string | null
    outline?: string | null
    price?: number | null
    image_url?: string | null
    in_stock?: boolean | null
  }[]
  categories: { name: string }[]
  brands: { name: string }[]
  articles: {
    title: string
    category?: string | null
    image_url?: string | null
    topics?: string[]
    created_at?: string | null
    updated_at?: string | null
  }[]
}

const PreviewDropdown = ({
  query,
  result,
  d,
  onPickWord
}: {
  query: string
  result: PreviewResult | undefined
  d: SuggestDeviceDesign
  onPickWord: (term: string) => void
}) => {
  // 「在庫切れ商品を表示対象にする」がOFFのデバイスは在庫切れ(in_stock=false)を除外し、
  // 保存前のドラフト設定でも即時にプレビューへ反映する（在庫不明=nullは対象外）
  const products =
    result && !d.show_out_of_stock
      ? result.products.filter((p) => p.in_stock !== false)
      : (result?.products ?? [])
  const any =
    result &&
    (result.words.length ||
      products.length ||
      result.categories.length ||
      result.brands.length ||
      result.articles.length ||
      result.did_you_mean)
  // 入力前（空欄）でも、人気キーワード等が返れば表示する。何も無ければ非表示。
  if (!any)
    return query.trim() ? (
      <div className='w2sg-box w2sg-empty'>「{query}」に一致する候補がありません</div>
    ) : null
  return (
    <div className='w2sg-box'>
      {!query.trim() && (
        <div className='w2sg-head' style={{ paddingTop: 8 }}>
          入力前の人気キーワード
        </div>
      )}
      {result?.did_you_mean && (
        <div className='w2sg-dym'>
          <span>もしかして：</span>
          <b>{result.did_you_mean}</b>
        </div>
      )}
      {result?.words && result.words.length > 0 && (
        <div className='w2sg-sec'>
          <div className='w2sg-head'>ワード</div>
          {result.words.map((w, i) => {
            const hasUrl = !!w.url
            return (
              <div
                className='w2sg-item'
                key={`w${i}`}
                title={
                  hasUrl ? 'クリックでリンク遷移（プレビューでは無効）' : 'クリックで検索窓に入力'
                }
                // mousedownでフォーカスを保持しつつ、URL無しのワードは検索窓へ入力（実挙動と同じ）
                onMouseDown={(e) => {
                  e.preventDefault()
                  if (!hasUrl) onPickWord(w.term)
                }}
              >
                <span className='w2sg-w'>
                  {w.pinned ? '📌 ' : ''}
                  {w.term}
                  {hasUrl ? ' 🔗' : ''}
                </span>
              </div>
            )
          })}
        </div>
      )}
      {products.length > 0 && (
        <div className='w2sg-sec'>
          <div className='w2sg-head'>商品</div>
          {products.map((p, i) => (
            <div className='w2sg-item' key={`p${i}`}>
              {d.show_thumb && p.image_url && (
                <img className='w2sg-thumb' src={p.image_url} alt='' loading='lazy' />
              )}
              <div className='w2sg-meta'>
                {d.show_product_name && <div className='w2sg-name'>{p.name}</div>}
                {d.show_catchcopy && p.catchcopy && (
                  <div className='w2sg-catch'>{trunc(p.catchcopy, d.catchcopy_length)}</div>
                )}
                {d.show_outline && p.outline && (
                  <div className='w2sg-outline'>{trunc(p.outline, d.outline_length)}</div>
                )}
                {(d.show_price || d.show_stock) && (
                  <div className='w2sg-psub'>
                    {d.show_price && p.price != null && (
                      <span className='w2sg-price'>¥{p.price.toLocaleString('ja-JP')}</span>
                    )}
                    {d.show_stock && p.in_stock != null && (
                      <span className='w2sg-stock'>{p.in_stock ? '在庫あり' : '在庫なし'}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {result?.categories && result.categories.length > 0 && (
        <div className='w2sg-sec'>
          <div className='w2sg-head'>カテゴリ</div>
          {result.categories.map((c, i) => (
            <div className='w2sg-item' key={`c${i}`}>
              <span className='w2sg-a'>{c.name}</span>
            </div>
          ))}
        </div>
      )}
      {result?.brands && result.brands.length > 0 && (
        <div className='w2sg-sec'>
          <div className='w2sg-head'>ブランド</div>
          {result.brands.map((b, i) => (
            <div className='w2sg-item' key={`b${i}`}>
              <span className='w2sg-a'>{b.name}</span>
            </div>
          ))}
        </div>
      )}
      {result?.articles && result.articles.length > 0 && (
        <div className='w2sg-sec'>
          <div className='w2sg-head'>記事</div>
          {result.articles.map((a, i) => (
            <div className='w2sg-item' key={`a${i}`}>
              {d.show_article_thumb && a.image_url && (
                <img className='w2sg-thumb' src={a.image_url} alt='' loading='lazy' />
              )}
              <div className='w2sg-meta'>
                {d.show_article_category && a.category && (
                  <div className='w2sg-acat'>{a.category}</div>
                )}
                {d.show_article_title && <div className='w2sg-name'>{a.title}</div>}
                {d.show_article_topics && a.topics && a.topics.length > 0 && (
                  <div className='w2sg-topics'>
                    {a.topics.map((x) => trunc(x, d.topic_length)).join(' / ')}
                  </div>
                )}
                {((d.show_article_created && a.created_at) ||
                  (d.show_article_updated && a.updated_at)) && (
                  <div className='w2sg-adate'>
                    {[
                      d.show_article_created && a.created_at
                        ? '登録 ' + a.created_at.slice(0, 10)
                        : '',
                      d.show_article_updated && a.updated_at
                        ? '更新 ' + a.updated_at.slice(0, 10)
                        : ''
                    ]
                      .filter(Boolean)
                      .join(' ・ ')}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// A-4: 取得待ちの間、直前の結果を「今入力中の文字」で部分一致フィルタして即時に絞る（楽観的表示）。
// 日本語の正規化差でヒットしない場合は絞り込まず直前結果をそのまま見せる（誤って空表示にしない）。
const narrowResult = (
  result: PreviewResult | undefined,
  typed: string
): PreviewResult | undefined => {
  if (!result) return result
  const t = typed.trim().toLowerCase()
  if (!t) return result
  const has = (s: string | null | undefined) => !!s && s.toLowerCase().includes(t)
  const words = result.words.filter((w) => has(w.term))
  const products = result.products.filter((p) => has(p.name) || has(p.catchcopy) || has(p.outline))
  const categories = result.categories.filter((c) => has(c.name))
  const brands = result.brands.filter((b) => has(b.name))
  const articles = result.articles.filter((a) => has(a.title) || has(a.category))
  const anyLeft =
    words.length || products.length || categories.length || brands.length || articles.length
  // 何も残らない（＝正規化差でヒットしない等）ときは絞らず直前結果を返す
  if (!anyLeft) return result
  return { ...result, did_you_mean: null, words, products, categories, brands, articles }
}

// 1デバイス分のプレビュー（実APIをデバイス指定で取得し、そのデバイスのデザインで描画）
const DeviceBox = ({
  siteId,
  device,
  query,
  typed,
  d,
  phone,
  onPickWord
}: {
  siteId: number
  device: 'pc' | 'sp'
  query: string
  typed: string
  d: SuggestDeviceDesign
  phone?: boolean
  onPickWord: (term: string) => void
}) => {
  // 空欄でも取得する（入力前の人気キーワードをプレビューするため）
  const { data: result } = useSuggestPreviewGetApi({ site_id: siteId, q: query, device })
  // 入力が取得中クエリより先行している間（デバウンス待ち）は楽観的に絞り込んで即応させる
  const displayResult =
    typed.trim() !== query.trim()
      ? narrowResult(result as PreviewResult | undefined, typed)
      : (result as PreviewResult | undefined)
  const themeCss = d.theme_color
    ? `.w2sgp-scope .w2sg-price{color:${d.theme_color}}.w2sgp-scope .w2sg-dym b{color:${d.theme_color}}`
    : ''
  const dd = (
    <div className='w2sgp-scope'>
      <style>{PREVIEW_BASE_CSS + themeCss + (d.custom_css || '')}</style>
      <PreviewDropdown query={query} result={displayResult} d={d} onPickWord={onPickWord} />
    </div>
  )
  if (!phone) return dd
  return (
    <div className='w-[320px] rounded-[36px] border-[10px] border-neutral-800 bg-neutral-800 shadow-xl'>
      <div className='overflow-hidden rounded-[26px] bg-white'>
        <div className='flex items-center justify-center bg-neutral-800 py-1.5'>
          <div className='h-1 w-16 rounded-full bg-neutral-600' />
        </div>
        <div className='p-3'>
          <div className='border-border-input mb-2 flex items-center gap-2 rounded-full border px-3 py-1.5'>
            <Text size={12} color={TextColor.SECONDARY}>
              🔍 {query || '検索'}
            </Text>
          </div>
          {dd}
        </div>
      </div>
    </div>
  )
}

const PreviewArea = ({
  siteId,
  pc,
  sp,
  pcOn,
  spOn,
  setPc,
  setSp
}: {
  siteId: number
  pc: SuggestDeviceDesign
  sp: SuggestDeviceDesign
  pcOn: boolean
  spOn: boolean
  setPc: (k: keyof SuggestDeviceDesign, v: string | number | boolean) => void
  setSp: (k: keyof SuggestDeviceDesign, v: string | number | boolean) => void
}) => {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [focused, setFocused] = useState(false)
  // プレビューで表示するデバイス（PC / SP を切替表示。同時表示はしない）
  const [device, setDevice] = useState<'pc' | 'sp'>('pc')
  const inputRef = useRef<HTMLInputElement | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onChange = (q: string) => {
    setQuery(q)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setDebounced(q), 100)
  }
  // ワードクリック：検索窓に入力（実挙動と同じ）。フォーカスを保持して候補を更新する。
  const pickWord = (term: string) => {
    setQuery(term)
    setDebounced(term)
    inputRef.current?.focus()
  }
  // 実際にプレビュー表示するデバイス。選択中デバイスがOFFなら、ONの側へフォールバックする
  const shown: 'pc' | 'sp' | null =
    device === 'pc' && pcOn
      ? 'pc'
      : device === 'sp' && spOn
        ? 'sp'
        : pcOn
          ? 'pc'
          : spOn
            ? 'sp'
            : null
  // タブ切替時は入力へフォーカスを戻し、プレビューを開いたまま保つ
  const pickDevice = (next: 'pc' | 'sp') => {
    setDevice(next)
    inputRef.current?.focus()
  }
  return (
    <Area>
      <Heading>ライブプレビュー＆デザイン</Heading>
      <AreaContent>
        {(pcOn || spOn) && (
          <TabList>
            <Tab selected={shown === 'pc'} disabled={!pcOn} onClick={() => pickDevice('pc')}>
              💻 PC
            </Tab>
            <Tab selected={shown === 'sp'} disabled={!spOn} onClick={() => pickDevice('sp')}>
              📱 SP
            </Tab>
          </TabList>
        )}
        <input
          ref={inputRef}
          className={`${textInput} mt-2 mb-2 w-full`}
          value={query}
          placeholder='プレビュー: 検索窓にフォーカスすると候補が出ます'
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            if (blurTimer.current) clearTimeout(blurTimer.current)
            setFocused(true)
          }}
          onBlur={() => {
            // クリック確定（mousedown）を待ってから閉じる
            blurTimer.current = setTimeout(() => setFocused(false), 150)
          }}
        />
        {focused && shown === 'pc' && (
          <div className='mt-2'>
            <DeviceBox
              siteId={siteId}
              device='pc'
              query={debounced}
              typed={query}
              d={pc}
              onPickWord={pickWord}
            />
          </div>
        )}
        {focused && shown === 'sp' && (
          <div className='mt-2'>
            <DeviceBox
              siteId={siteId}
              device='sp'
              query={debounced}
              typed={query}
              d={sp}
              phone
              onPickWord={pickWord}
            />
          </div>
        )}
        {shown && (
          <div className='mt-4'>
            <Text size={12} color={TextColor.SECONDARY}>
              テーマ・CSSはPC/SPで別管理です（表示項目・件数は種別表で調整）。
            </Text>
            <div className='border-border-input mt-2 rounded-lg border p-3'>
              <Text size={14} weight='bold'>
                {shown === 'pc' ? '💻 PC デザイン' : '📱 SP デザイン'}
              </Text>
              <div className='mt-2'>
                {shown === 'pc' ? (
                  <DeviceThemeCss key='pc-design' d={pc} set={setPc} />
                ) : (
                  <DeviceThemeCss key='sp-design' d={sp} set={setSp} />
                )}
              </div>
            </div>
            <CssClassReference />
          </div>
        )}
        {!pcOn && !spOn && (
          <Text size={12} color={TextColor.SECONDARY}>
            配信デバイスがすべてOFFです。「配信デバイス」でON にするとプレビューできます。
          </Text>
        )}
      </AreaContent>
    </Area>
  )
}

// カスタムCSSで使えるクラス一覧（埋め込みJS w2suggest.js / プレビューと一致）
const CSS_CLASS_REF: { cls: string; desc: string }[] = [
  { cls: '.w2sg-box', desc: 'サジェスト全体の枠（背景・枠線・角丸・影）' },
  { cls: '.w2sg-dym', desc: '「もしかして」バー' },
  { cls: '.w2sg-sec', desc: '各セクション（ワード/商品/カテゴリ等）の区切り' },
  { cls: '.w2sg-head', desc: 'セクション見出し（「ワード」「商品」等）' },
  { cls: '.w2sg-item', desc: '候補1件の行（ホバー背景もここ）' },
  { cls: '.w2sg-thumb', desc: '商品・記事のサムネイル画像' },
  { cls: '.w2sg-name', desc: '商品名・記事タイトル' },
  { cls: '.w2sg-catch', desc: '商品のキャッチコピー' },
  { cls: '.w2sg-outline', desc: '商品概要' },
  { cls: '.w2sg-price', desc: '価格' },
  { cls: '.w2sg-stock', desc: '在庫バッジ' },
  { cls: '.w2sg-w', desc: 'ワード候補のテキスト' },
  { cls: '.w2sg-a', desc: 'カテゴリ・ブランド候補のテキスト' },
  { cls: '.w2sg-acat', desc: '記事のカテゴリ' },
  { cls: '.w2sg-topics', desc: '記事のトピック一覧' },
  { cls: '.w2sg-adate', desc: '記事の登録日・更新日' }
]

// ── 項目編集（CSS不要のフォーム）用スキーマと、CSS文字列との相互変換 ─────────
type CtrlType = 'color' | 'px' | 'weight' | 'shadow' | 'bstyle'
interface DesignCtrl {
  prop: string
  label: string
  type: CtrlType
}
interface DesignGroup {
  sel: string
  label: string
  desc: string
  ctrls: DesignCtrl[]
}

const WEIGHT_OPTS = [
  { v: 'normal', n: '標準' },
  { v: '600', n: '中太' },
  { v: 'bold', n: '太字' }
]
const SHADOW_OPTS = [
  { v: 'none', n: 'なし' },
  { v: '0 1px 3px rgba(0,0,0,0.08)', n: '弱' },
  { v: '0 4px 12px rgba(0,0,0,0.12)', n: '中' },
  { v: '0 8px 24px rgba(0,0,0,0.18)', n: '強' }
]
const BSTYLE_OPTS = [
  { v: 'solid', n: '実線' },
  { v: 'dashed', n: '破線' },
  { v: 'dotted', n: '点線' }
]

const mkCtrl = (prop: string, label: string, type: CtrlType): DesignCtrl => ({ prop, label, type })
const DESIGN_GROUPS: DesignGroup[] = [
  {
    sel: '.w2sg-box',
    label: 'サジェスト全体の枠',
    desc: '背景・枠線・角丸・影',
    ctrls: [
      mkCtrl('background-color', '背景色', 'color'),
      mkCtrl('border-color', '枠線の色', 'color'),
      mkCtrl('border-width', '枠線の太さ', 'px'),
      mkCtrl('border-style', '枠線の種類', 'bstyle'),
      mkCtrl('border-radius', '角丸', 'px'),
      mkCtrl('box-shadow', '影', 'shadow'),
      mkCtrl('padding', '内側の余白', 'px')
    ]
  },
  {
    sel: '.w2sg-dym',
    label: '「もしかして」バー',
    desc: '',
    ctrls: [
      mkCtrl('color', '文字色', 'color'),
      mkCtrl('background-color', '背景色', 'color'),
      mkCtrl('font-size', '文字サイズ', 'px')
    ]
  },
  {
    sel: '.w2sg-sec',
    label: 'セクション区切り',
    desc: 'ワード/商品/カテゴリ等',
    ctrls: [
      mkCtrl('border-top-color', '区切り線の色', 'color'),
      mkCtrl('border-top-width', '区切り線の太さ', 'px'),
      mkCtrl('padding', '内側の余白', 'px')
    ]
  },
  {
    sel: '.w2sg-head',
    label: 'セクション見出し',
    desc: '「ワード」「商品」等',
    ctrls: [
      mkCtrl('color', '文字色', 'color'),
      mkCtrl('background-color', '背景色', 'color'),
      mkCtrl('font-size', '文字サイズ', 'px'),
      mkCtrl('font-weight', '太さ', 'weight')
    ]
  },
  {
    sel: '.w2sg-item',
    label: '候補1件の行',
    desc: '',
    ctrls: [
      mkCtrl('color', '文字色', 'color'),
      mkCtrl('background-color', '背景色', 'color'),
      mkCtrl('padding', '内側の余白', 'px')
    ]
  },
  {
    sel: '.w2sg-thumb',
    label: 'サムネイル画像',
    desc: '商品・記事',
    ctrls: [
      mkCtrl('width', '幅', 'px'),
      mkCtrl('height', '高さ', 'px'),
      mkCtrl('border-radius', '角丸', 'px')
    ]
  },
  {
    sel: '.w2sg-name',
    label: '商品名・記事タイトル',
    desc: '',
    ctrls: [
      mkCtrl('color', '文字色', 'color'),
      mkCtrl('font-size', '文字サイズ', 'px'),
      mkCtrl('font-weight', '太さ', 'weight')
    ]
  },
  {
    sel: '.w2sg-catch',
    label: 'キャッチコピー',
    desc: '商品',
    ctrls: [mkCtrl('color', '文字色', 'color'), mkCtrl('font-size', '文字サイズ', 'px')]
  },
  {
    sel: '.w2sg-outline',
    label: '商品概要',
    desc: '',
    ctrls: [mkCtrl('color', '文字色', 'color'), mkCtrl('font-size', '文字サイズ', 'px')]
  },
  {
    sel: '.w2sg-price',
    label: '価格',
    desc: '',
    ctrls: [
      mkCtrl('color', '文字色', 'color'),
      mkCtrl('font-size', '文字サイズ', 'px'),
      mkCtrl('font-weight', '太さ', 'weight')
    ]
  },
  {
    sel: '.w2sg-stock',
    label: '在庫バッジ',
    desc: '',
    ctrls: [
      mkCtrl('color', '文字色', 'color'),
      mkCtrl('background-color', '背景色', 'color'),
      mkCtrl('font-size', '文字サイズ', 'px'),
      mkCtrl('border-radius', '角丸', 'px')
    ]
  },
  {
    sel: '.w2sg-w',
    label: 'ワード候補のテキスト',
    desc: '',
    ctrls: [
      mkCtrl('color', '文字色', 'color'),
      mkCtrl('font-size', '文字サイズ', 'px'),
      mkCtrl('font-weight', '太さ', 'weight')
    ]
  },
  {
    sel: '.w2sg-a',
    label: 'カテゴリ・ブランド候補',
    desc: '',
    ctrls: [mkCtrl('color', '文字色', 'color'), mkCtrl('font-size', '文字サイズ', 'px')]
  },
  {
    sel: '.w2sg-acat',
    label: '記事のカテゴリ',
    desc: '',
    ctrls: [mkCtrl('color', '文字色', 'color'), mkCtrl('font-size', '文字サイズ', 'px')]
  },
  {
    sel: '.w2sg-topics',
    label: '記事のトピック一覧',
    desc: '',
    ctrls: [mkCtrl('color', '文字色', 'color'), mkCtrl('font-size', '文字サイズ', 'px')]
  },
  {
    sel: '.w2sg-adate',
    label: '記事の登録日・更新日',
    desc: '',
    ctrls: [mkCtrl('color', '文字色', 'color'), mkCtrl('font-size', '文字サイズ', 'px')]
  }
]

const DESIGN_SELECTORS = new Set(DESIGN_GROUPS.map((g) => g.sel))

interface ParsedCss {
  rules: Record<string, Record<string, string>>
  extra: string
}

// custom_css を「既知セレクタの宣言(rules)」と「それ以外(extra)」に分解する。
// @media 等のネストや未知セレクタは extra として原文のまま保全し、項目編集で失わない。
const parseCss = (css: string): ParsedCss => {
  const rules: Record<string, Record<string, string>> = {}
  const extra: string[] = []
  const s = css || ''
  const n = s.length
  let i = 0
  while (i < n) {
    const open = s.indexOf('{', i)
    if (open === -1) {
      const rest = s.slice(i).trim()
      if (rest) extra.push(rest)
      break
    }
    const sel = s.slice(i, open).trim()
    let depth = 1
    let j = open + 1
    for (; j < n; j++) {
      const ch = s[j]
      if (ch === '{') depth++
      else if (ch === '}') {
        depth--
        if (depth === 0) break
      }
    }
    const body = s.slice(open + 1, j)
    const full = s.slice(i, Math.min(j + 1, n))
    if (DESIGN_SELECTORS.has(sel) && body.indexOf('{') === -1) {
      const decls: Record<string, string> = { ...(rules[sel] || {}) }
      body.split(';').forEach((dec) => {
        const ci = dec.indexOf(':')
        if (ci > 0) {
          const p = dec.slice(0, ci).trim()
          const v = dec.slice(ci + 1).trim()
          if (p && v) decls[p] = v
        }
      })
      rules[sel] = decls
    } else if (full.trim()) {
      extra.push(full.trim())
    }
    i = j + 1
  }
  return { rules, extra: extra.join('\n\n') }
}

// rules を CSS 文字列へ整形。extra は末尾に保全。枠線は太さ/色のみ指定時に solid を自動補完。
const buildCss = (parsed: ParsedCss): string => {
  const blocks: string[] = []
  DESIGN_GROUPS.forEach(({ sel }) => {
    const src = parsed.rules[sel]
    if (!src) return
    const p: Record<string, string> = { ...src }
    if ((p['border-width'] || p['border-color']) && !p['border-style']) p['border-style'] = 'solid'
    if ((p['border-top-width'] || p['border-top-color']) && !p['border-top-style'])
      p['border-top-style'] = 'solid'
    const keys = Object.keys(p)
    if (keys.length === 0) return
    const decls = keys.map((k) => `  ${k}: ${p[k]};`).join('\n')
    blocks.push(`${sel} {\n${decls}\n}`)
  })
  const managed = blocks.join('\n')
  const ex = (parsed.extra || '').trim()
  return [managed, ex].filter(Boolean).join('\n\n')
}

const defaultForCtrl = (ctrl: DesignCtrl): string => {
  switch (ctrl.type) {
    case 'color':
      return ctrl.prop.includes('background')
        ? '#ffffff'
        : ctrl.prop.includes('border')
          ? '#e5e5e5'
          : '#333333'
    case 'px':
      if (ctrl.prop.includes('border') && ctrl.prop.includes('width')) return '1px'
      if (ctrl.prop === 'border-radius') return '8px'
      if (ctrl.prop === 'padding') return '8px'
      if (ctrl.prop === 'width' || ctrl.prop === 'height') return '44px'
      return '14px'
    case 'weight':
      return 'bold'
    case 'shadow':
      return '0 4px 12px rgba(0,0,0,0.12)'
    case 'bstyle':
      return 'solid'
  }
}

// 1デバイス分のデザイン編集：項目編集（CSS不要）と CSS直接編集を切替。
// 項目編集の変更は custom_css に即時反映され、ライブプレビューにも反映される。
const DeviceThemeCss = ({
  d,
  set
}: {
  d: SuggestDeviceDesign
  set: (k: keyof SuggestDeviceDesign, v: string | number | boolean) => void
}) => {
  const [mode, setMode] = useState<'form' | 'css'>('form')
  // 現在の custom_css を解析して項目編集の初期値（＝現在値）にする
  const [parsed, setParsed] = useState<ParsedCss>(() => parseCss(d.custom_css || ''))

  // CSS直接編集の内容を項目編集へ取り込む（モード切替時に再解析）
  const toForm = () => {
    setParsed(parseCss(d.custom_css || ''))
    setMode('form')
  }
  const setProp = (sel: string, prop: string, value: string | null) => {
    const rules = { ...parsed.rules }
    const sub = { ...(rules[sel] || {}) }
    if (value === null) delete sub[prop]
    else sub[prop] = value
    if (Object.keys(sub).length === 0) delete rules[sel]
    else rules[sel] = sub
    const next: ParsedCss = { rules, extra: parsed.extra }
    setParsed(next)
    set('custom_css', buildCss(next))
  }

  const renderCtrl = (sel: string, ctrl: DesignCtrl) => {
    const val = parsed.rules[sel]?.[ctrl.prop]
    const on = val !== undefined
    const opts =
      ctrl.type === 'weight' ? WEIGHT_OPTS : ctrl.type === 'shadow' ? SHADOW_OPTS : BSTYLE_OPTS
    return (
      <label key={ctrl.prop} className='flex min-h-6 items-center gap-2'>
        <input
          type='checkbox'
          className='accent-[var(--color-fill-brand)]'
          checked={on}
          onChange={(e) => setProp(sel, ctrl.prop, e.target.checked ? defaultForCtrl(ctrl) : null)}
        />
        <span className='text-secondary w-24 shrink-0 text-[11px]'>{ctrl.label}</span>
        {!on ? (
          <span className='text-tertiary text-[11px]'>既定のまま</span>
        ) : ctrl.type === 'color' ? (
          <span className='flex items-center gap-1'>
            <input
              type='color'
              className='border-border-input h-6 w-8 rounded border'
              value={/^#[0-9a-fA-F]{6}$/.test(val) ? val : '#333333'}
              onChange={(e) => setProp(sel, ctrl.prop, e.target.value)}
            />
            <input
              className='border-border-input text-size-13 bg-input h-6 w-20 rounded-lg border px-2 font-mono'
              value={val}
              onChange={(e) => setProp(sel, ctrl.prop, e.target.value)}
            />
          </span>
        ) : ctrl.type === 'px' ? (
          <span className='flex items-center gap-1'>
            <input
              type='number'
              min={0}
              className='border-border-input text-size-13 bg-input h-6 w-16 rounded-lg border px-2'
              value={parseInt(val, 10) || 0}
              onChange={(e) =>
                setProp(sel, ctrl.prop, `${e.target.value === '' ? 0 : e.target.value}px`)
              }
            />
            <span className='text-tertiary text-[11px]'>px</span>
          </span>
        ) : (
          <select
            className='border-border-input text-size-13 bg-input h-6 rounded-lg border px-1'
            value={val}
            onChange={(e) => setProp(sel, ctrl.prop, e.target.value)}
          >
            {opts.map((o) => (
              <option key={o.v} value={o.v}>
                {o.n}
              </option>
            ))}
          </select>
        )}
      </label>
    )
  }

  return (
    <div className='flex flex-col gap-3'>
      <div>
        <Text size={12} weight='bold'>
          テーマカラー
        </Text>
        <div className='mt-1 flex items-center gap-2'>
          <input
            type='color'
            className='border-border-input h-8 w-10 rounded border'
            value={d.theme_color || '#0e8e80'}
            onChange={(e) => set('theme_color', e.target.value)}
          />
          <input
            className={`${textInput} w-28`}
            value={d.theme_color ?? ''}
            placeholder='#0e8e80'
            onChange={(e) => set('theme_color', e.target.value)}
          />
        </div>
      </div>

      <div className='border-border-input flex w-fit rounded-lg border p-0.5'>
        <button
          type='button'
          onClick={toForm}
          className={`rounded-md px-3 py-1 text-[12px] font-semibold ${
            mode === 'form' ? 'bg-fill-brand text-on-bg-fill' : 'text-secondary'
          }`}
        >
          項目で編集
        </button>
        <button
          type='button'
          onClick={() => setMode('css')}
          className={`rounded-md px-3 py-1 text-[12px] font-semibold ${
            mode === 'css' ? 'bg-fill-brand text-on-bg-fill' : 'text-secondary'
          }`}
        >
          CSSで編集
        </button>
      </div>

      {mode === 'form' ? (
        <div className='flex flex-col gap-1.5'>
          <Text size={12} color={TextColor.SECONDARY}>
            変更したい項目だけチェックを入れて調整します（未チェックは既定の見た目のまま）。内容は「CSSで編集」にも反映されます。
          </Text>
          {DESIGN_GROUPS.map((g) => {
            const count = parsed.rules[g.sel] ? Object.keys(parsed.rules[g.sel]).length : 0
            return (
              <details key={g.sel} className='border-border-input rounded-lg border'>
                <summary className='flex cursor-pointer items-center gap-2 px-2.5 py-1.5 select-none'>
                  <Text size={12} weight='bold'>
                    {g.label}
                  </Text>
                  {g.desc && <span className='text-tertiary text-[10px]'>{g.desc}</span>}
                  <span className='text-tertiary ml-auto font-mono text-[10px]'>{g.sel}</span>
                  {count > 0 && (
                    <span className='bg-surface-emphasis text-emphasis rounded px-1.5 py-0.5 text-[9px] font-semibold'>
                      {count}
                    </span>
                  )}
                </summary>
                <div className='flex flex-col gap-1 px-2.5 pt-1 pb-2.5'>
                  {g.ctrls.map((ctrl) => renderCtrl(g.sel, ctrl))}
                </div>
              </details>
            )
          })}
        </div>
      ) : (
        <div>
          <Text size={12} weight='bold'>
            カスタムCSS
          </Text>
          <textarea
            className={`${textInput} mt-1 h-40 w-full font-mono`}
            value={d.custom_css ?? ''}
            placeholder={'.w2sg-name{font-weight:700}\n.w2sg-price{color:#c0392b}'}
            onChange={(e) => set('custom_css', e.target.value)}
          />
        </div>
      )}
    </div>
  )
}

// カスタムCSSで使えるクラス名の早見表（PC/SP共通なので1回だけ表示）
const CssClassReference = () => (
  <details className='mt-3'>
    <summary className='text-secondary hover:text-primary cursor-pointer text-[12px] select-none'>
      カスタムCSSで使えるクラス名の一覧を見る
    </summary>
    <Text size={12} color={TextColor.SECONDARY}>
      下のクラスを対象にCSSを書くと、右のライブプレビューに即時反映されます。プレビュー上で右クリック→「検証」でも構造を確認できます。
    </Text>
    <div className='border-border-input mt-2 overflow-hidden rounded-lg border'>
      {CSS_CLASS_REF.map((r, i) => (
        <div
          key={r.cls}
          className={`flex flex-wrap items-center gap-x-3 px-3 py-1.5 ${
            i % 2 ? 'bg-fill-surface-tertiary' : ''
          }`}
        >
          <code className='text-[12px]'>{r.cls}</code>
          <Text size={12} color={TextColor.SECONDARY}>
            {r.desc}
          </Text>
        </div>
      ))}
    </div>
    <div className='mt-1'>
      <Text size={12} color={TextColor.SECONDARY}>
        ※ クラスは全サイト共通の接頭辞 <code className='text-[12px]'>.w2sg-</code>{' '}
        を使用。ホスト側CSSと衝突しにくいよう隔離されています。
      </Text>
    </div>
  </details>
)

// --- タグ発行（検索窓セレクタ＋埋め込みタグ＋CV計測タグ） ---
const TagPanel = ({ siteId }: { siteId: number }) => {
  const { data, error, isLoading, mutate: mutTag } = useSuggestEmbedTagGetApi({ site_id: siteId })
  const { data: delivery, mutate: mutDelivery } = useSuggestDeliveryGetApi({ site_id: siteId })
  const showSaved = useShowSavedToast({ editing: true })
  const { trigger: updD, isMutating } = useSuggestDeliveryUpdateApi({
    onSuccess: () => {
      showSaved('検索窓セレクタ')
      mutDelivery()
      mutTag()
    }
  })
  const [sel, setSel] = useState<string | null>(null)
  const selVal = sel ?? delivery?.input_selector ?? ''
  const [copied, setCopied] = useState(false)
  const [cvCopied, setCvCopied] = useState(false)
  const copyText = (text: string, mark: (v: boolean) => void) => {
    navigator.clipboard?.writeText(text).then(
      () => {
        mark(true)
        setTimeout(() => mark(false), 1500)
      },
      () => {}
    )
  }
  return (
    <DetailPageLoader error={error} data={!isLoading && data}>
      {data && (
        <>
          <Area>
            <Heading>検索窓セレクタ</Heading>
            <AreaContent>
              <Text size={14}>
                サジェストを表示したい検索入力ボックスのCSSセレクタを指定します。idなら{' '}
                <code>#id名</code>、classなら <code>.class名</code>（例: 入力欄が
                id=&quot;global_freeWordSearch&quot; なら <code>#global_freeWordSearch</code>
                ）。下の埋め込みタグの <b>data-input</b> に反映されます。
              </Text>
              <div className='mt-3 flex flex-wrap items-center gap-2'>
                <input
                  className={`${textInput} min-w-[240px]`}
                  value={selVal}
                  placeholder='#site-search'
                  onChange={(e) => setSel(e.target.value)}
                />
                <Button
                  color={ButtonColor.PRIMARY}
                  onClick={() => updD({ site_id: siteId, input_selector: selVal })}
                  loading={isMutating}
                  disabled={sel === null || selVal === (delivery?.input_selector ?? '')}
                >
                  保存
                </Button>
              </div>
            </AreaContent>
          </Area>

          <Area>
            <Heading>埋め込みタグ</Heading>
            <AreaContent>
              <div className='flex flex-col gap-1'>
                <Text size={14}>
                  下のタグをコピーし、検索窓があるページのHTML（通常マスターページ内に）貼り付けると、サジェストが有効になります（設置先は
                  &lt;head&gt; 内推奨）。<b>data-input</b> は上で設定した検索窓セレクタが入ります。
                </Text>
                <Text size={12} color={TextColor.SECONDARY}>
                  ・サジェスト候補の種類（ワード/商品/カテゴリ等）や表示件数は、PC/SPで別々に「設定・デザイン」タブで制御します。設定を変えてもタグの貼り替えは不要です。
                </Text>
              </div>
              <pre className='bg-fill-surface-tertiary border-border-input mt-3 overflow-x-auto rounded-lg border p-4 text-[12px] leading-relaxed whitespace-pre-wrap'>
                {data.snippet}
              </pre>
              <div className='mt-3 flex items-center gap-3'>
                <Button
                  color={ButtonColor.SECONDARY}
                  onClick={() => copyText(data.snippet, setCopied)}
                >
                  {copied ? 'コピーしました' : 'コピー'}
                </Button>
                <Text size={12} color={TextColor.SECONDARY}>
                  現在の検索窓セレクタ: {data.input_selector}
                </Text>
              </div>

              <div className='mt-6'>
                <Text size={14} weight='bold'>
                  コンバージョン計測タグ（任意・ダッシュボードの「経由CVR」用）
                </Text>
                <Text size={12} color={TextColor.SECONDARY}>
                  注文完了（サンクス）ページに設置すると、サジェスト経由の購入を計測できます。
                  <code>{'{{注文番号}}'}</code>{' '}
                  等は各サイトのテンプレート変数に置き換えてください。RecoMedia
                  の有無に関係なく動作します。
                </Text>
                <pre className='bg-fill-surface-tertiary border-border-input mt-2 overflow-x-auto rounded-lg border p-4 text-[12px] leading-relaxed whitespace-pre-wrap'>
                  {data.cv_snippet}
                </pre>
                <div className='mt-2'>
                  <Button
                    color={ButtonColor.SECONDARY}
                    onClick={() => copyText(data.cv_snippet, setCvCopied)}
                  >
                    {cvCopied ? 'コピーしました' : 'コピー'}
                  </Button>
                </div>
              </div>
            </AreaContent>
          </Area>
        </>
      )}
    </DetailPageLoader>
  )
}

// 配信対象URL：サジェストが動作するURLは「サイト管理」に登録したサイトURLに固定。
// 編集・削除は不可。有効/無効の切替のみ可能。
const KeyManagerPanel = ({ siteId }: { siteId: number }) => {
  const { data: keys, mutate } = useSuggestWidgetKeysGetApi({ site_id: siteId })
  const { data: site } = useSiteGetApi({ site_id: siteId })
  const { trigger: update } = useSuggestWidgetKeyUpdateApi({ onSuccess: () => mutate() })

  return (
    <Area>
      <Heading>配信対象URL</Heading>
      <AreaContent>
        <Text size={12} color={TextColor.SECONDARY}>
          サジェストは「サイト管理」に登録されたサイトURLでのみ動作します（URLの変更はサイト管理から行います。ここでは編集できません）。有効/無効で配信のON/OFFを切り替えられます。
        </Text>
        <div className='mt-3 flex flex-col gap-3'>
          {(keys ?? []).map((k) => (
            <div
              key={k.id}
              className='border-border-input flex flex-wrap items-center gap-3 rounded-lg border p-3'
            >
              <div className='min-w-0 flex-1'>
                <Text size={12} color={TextColor.SECONDARY}>
                  配信対象URL
                </Text>
                <Text weight='bold'>{site?.domain || '（サイト管理で未登録）'}</Text>
              </div>
              <Text size={12} color={k.enabled ? TextColor.SUCCESS : TextColor.SECONDARY}>
                {k.enabled ? '有効' : '無効'}
              </Text>
              <CheckBox
                type={CheckBoxType.TOGGLE_BUTTON}
                name={`key-${k.id}`}
                checked={k.enabled}
                onChange={(v) => update({ site_id: siteId, key_id: k.id, enabled: v })}
              />
            </div>
          ))}
          {keys && keys.length === 0 && (
            <Text color={TextColor.SECONDARY}>
              配信設定はまだありません（タグ発行時に自動作成されます）。
            </Text>
          )}
        </div>
      </AreaContent>
    </Area>
  )
}

// --- 公開管理（配信の有効化＋配信対象URL） ---
const PublishContent = ({ siteId }: { siteId: number }) => {
  const { data: settings, error, isLoading, mutate } = useSuggestSettingsGetApi({ site_id: siteId })
  const showSaved = useShowSavedToast({ editing: true })
  const { trigger, isMutating } = useSuggestSettingsUpdateApi({
    onSuccess: () => {
      showSaved('配信状態')
      mutate()
    }
  })
  return (
    <DetailPageLoader error={error} data={!isLoading && settings}>
      {settings && (
        <>
          <Area>
            <Heading>サジェスト全体</Heading>
            <AreaContent>
              <SettingRow
                title='サジェストを有効にする'
                sub='OFFにすると全デバイスでサジェストを停止します（即時反映）'
              >
                <CheckBox
                  type={CheckBoxType.TOGGLE_BUTTON}
                  name='enable_master'
                  checked={!!settings.enable_master}
                  onChange={(v) => !isMutating && trigger({ site_id: siteId, enable_master: v })}
                />
              </SettingRow>
            </AreaContent>
          </Area>
          <KeyManagerPanel siteId={siteId} />
        </>
      )}
    </DetailPageLoader>
  )
}

// 表示する候補の種別：ON/OFF と PC/SP件数を1枠にまとめたマトリクス
type CountKey = 'count_word' | 'count_product' | 'count_category' | 'count_brand' | 'count_article'

// 展開時に出す表示項目（show_* トグル＋必要なら文字数）。lenKey があれば文字数入力を併設。
interface DisplayItem {
  key: keyof SuggestDeviceDesign
  label: string
  lenKey?: keyof SuggestDeviceDesign
}
const PRODUCT_ITEMS: DisplayItem[] = [
  { key: 'show_product_name', label: '商品名' },
  { key: 'show_catchcopy', label: 'キャッチコピー', lenKey: 'catchcopy_length' },
  { key: 'show_outline', label: '商品概要', lenKey: 'outline_length' },
  { key: 'show_thumb', label: 'サムネイル画像' },
  { key: 'show_price', label: '価格' },
  { key: 'show_stock', label: '在庫' },
  { key: 'show_out_of_stock', label: '在庫切れ商品を表示対象にする' }
]
const ARTICLE_ITEMS: DisplayItem[] = [
  { key: 'show_article_title', label: 'タイトル' },
  { key: 'show_article_category', label: 'カテゴリ' },
  { key: 'show_article_thumb', label: 'サムネイル画像' },
  { key: 'show_article_topics', label: 'トピック', lenKey: 'topic_length' },
  { key: 'show_article_created', label: '登録日' },
  { key: 'show_article_updated', label: '更新日' }
]

const TYPE_ROWS: {
  label: string
  enableKey: keyof SuggestSettings
  countKey: CountKey
  max: number
  def: number
  items?: DisplayItem[]
}[] = [
  { label: 'ワード', enableKey: 'enable_word', countKey: 'count_word', max: 10, def: 5 },
  {
    label: '商品',
    enableKey: 'enable_product',
    countKey: 'count_product',
    max: 8,
    def: 4,
    items: PRODUCT_ITEMS
  },
  { label: 'カテゴリ', enableKey: 'enable_category', countKey: 'count_category', max: 8, def: 3 },
  { label: 'ブランド', enableKey: 'enable_brand', countKey: 'count_brand', max: 8, def: 3 },
  {
    label: '記事',
    enableKey: 'enable_article',
    countKey: 'count_article',
    max: 8,
    def: 3,
    items: ARTICLE_ITEMS
  }
]

// PC/SP それぞれの「表示トグル＋件数プルダウン」。表示は件数>0で表現し、
// OFF→0件 / ON→既定件数。件数の同期（enable_*）は保存時に行う。
const DeviceCountCell = ({
  on,
  count,
  max,
  def,
  keyName,
  onCount
}: {
  on: boolean
  count: number
  max: number
  def: number
  keyName: string
  onCount: (v: number) => void
}) => (
  <div className='flex items-center gap-2'>
    <CheckBox
      type={CheckBoxType.TOGGLE_BUTTON}
      name={keyName}
      checked={on}
      onChange={(v) => (v ? onCount(def) : onCount(0))}
    />
    {on ? (
      <Selector
        name={`${keyName}_count`}
        choices={countChoices(max)}
        value={count}
        onChange={(e) => onCount(Number(e.target.value))}
      />
    ) : (
      <Text size={14} color={TextColor.SECONDARY}>
        —
      </Text>
    )}
  </div>
)

const OffCell = ({ reason = '配信OFF' }: { reason?: string }) => (
  <Text size={12} color={TextColor.SECONDARY}>
    {reason}
  </Text>
)

// 1デバイス分の表示項目セル（トグル＋必要なら文字数）
const ItemDeviceCell = ({
  d,
  item,
  on,
  offReason,
  keyName,
  set
}: {
  d: SuggestDeviceDesign
  item: DisplayItem
  on: boolean
  offReason?: string
  keyName: string
  set: (k: keyof SuggestDeviceDesign, v: number | boolean) => void
}) => {
  // 親（デバイス・種別）がOFFなら、子だけONに見えるのを防ぐ。
  // 値は消さない。親を戻したときに設定し直させないため
  if (!on) return <OffCell reason={offReason} />
  const checked = !!d[item.key]
  return (
    <div className='flex items-center gap-2'>
      <CheckBox
        type={CheckBoxType.TOGGLE_BUTTON}
        name={keyName}
        checked={checked}
        onChange={(v) => set(item.key, v)}
      />
      {item.lenKey && checked && (
        <div className='flex items-center gap-1'>
          <input
            type='number'
            min={0}
            className={`${textInput} w-16`}
            value={d[item.lenKey] as number}
            onChange={(e) => set(item.lenKey as keyof SuggestDeviceDesign, Number(e.target.value))}
          />
          <Text size={12} color={TextColor.SECONDARY}>
            字
          </Text>
        </div>
      )}
    </div>
  )
}

const CandidateTypeMatrix = ({
  pc,
  sp,
  setPc,
  setSp,
  pcOn,
  spOn
}: {
  pc: SuggestDeviceDesign
  sp: SuggestDeviceDesign
  setPc: (k: keyof SuggestDeviceDesign, v: number | boolean) => void
  setSp: (k: keyof SuggestDeviceDesign, v: number | boolean) => void
  pcOn: boolean
  spOn: boolean
}) => {
  const [open, setOpen] = useState<Record<string, boolean>>({})
  return (
    <>
      <Heading>サジェスト項目の表示可否と表示件数</Heading>
      <AreaContent>
        <div className='grid grid-cols-[1fr_auto_auto] items-stretch gap-x-6'>
          <div />
          <Text size={12} color={pcOn ? TextColor.SECONDARY : TextColor.DISABLED}>
            💻 PC（表示・件数）
          </Text>
          <Text size={12} color={spOn ? TextColor.SECONDARY : TextColor.DISABLED}>
            📱 SP（表示・件数）
          </Text>
          {TYPE_ROWS.map((row) => {
            const pcCount = pc[row.countKey]
            const spCount = sp[row.countKey]
            const expanded = !!row.items && !!open[row.countKey]
            return (
              <Fragment key={row.countKey}>
                <div className='border-border-input flex items-center gap-2 border-t py-2'>
                  <Text weight='bold' size={14}>
                    {row.label}
                  </Text>
                  {row.items && (
                    <button
                      type='button'
                      className='text-secondary hover:text-primary text-[11px]'
                      onClick={() => setOpen((o) => ({ ...o, [row.countKey]: !o[row.countKey] }))}
                    >
                      {expanded ? '▲ 表示項目を閉じる' : '▼ 表示項目'}
                    </button>
                  )}
                </div>
                <div className='border-border-input flex items-center border-t py-2'>
                  {pcOn ? (
                    <DeviceCountCell
                      on={pcCount > 0}
                      count={pcCount}
                      max={row.max}
                      def={row.def}
                      keyName={`pc_${row.countKey}`}
                      onCount={(v) => setPc(row.countKey, v)}
                    />
                  ) : (
                    <OffCell />
                  )}
                </div>
                <div className='border-border-input flex items-center border-t py-2'>
                  {spOn ? (
                    <DeviceCountCell
                      on={spCount > 0}
                      count={spCount}
                      max={row.max}
                      def={row.def}
                      keyName={`sp_${row.countKey}`}
                      onCount={(v) => setSp(row.countKey, v)}
                    />
                  ) : (
                    <OffCell />
                  )}
                </div>
                {expanded &&
                  row.items &&
                  row.items.map((item) => (
                    <Fragment key={`${row.countKey}_${String(item.key)}`}>
                      <div className='bg-fill-surface-tertiary flex items-center py-1.5 pl-5'>
                        <Text size={14}>{item.label}</Text>
                      </div>
                      <div className='bg-fill-surface-tertiary flex items-center py-1.5'>
                        <ItemDeviceCell
                          d={pc}
                          item={item}
                          on={pcOn && pcCount > 0}
                          offReason={pcOn ? `${row.label}がOFF` : '配信OFF'}
                          keyName={`pc_${String(item.key)}`}
                          set={setPc}
                        />
                      </div>
                      <div className='bg-fill-surface-tertiary flex items-center py-1.5'>
                        <ItemDeviceCell
                          d={sp}
                          item={item}
                          on={spOn && spCount > 0}
                          offReason={spOn ? `${row.label}がOFF` : '配信OFF'}
                          keyName={`sp_${String(item.key)}`}
                          set={setSp}
                        />
                      </div>
                    </Fragment>
                  ))}
              </Fragment>
            )
          })}
        </div>
        <Text size={12} color={TextColor.SECONDARY}>
          ※
          配信OFFのデバイスは「配信デバイス」で再度ONにすると設定できます。表示をOFFにするとそのデバイスではその種別を出しません。商品・記事は
          ▼
          で各表示項目を設定できます（種別をOFFにすると配下の項目もOFF表示になります。設定値は保持され、種別を戻すと元に戻ります）。
        </Text>
      </AreaContent>
    </>
  )
}

// --- 人気キーワード（入力前の初期表示：固定ピン＋自動人気） ---
const PopularKeywordsContent = ({ siteId }: { siteId: number }) => {
  const { data: settings, mutate: mutS } = useSuggestSettingsGetApi({ site_id: siteId })
  const { data: manual, mutate: mutM } = useSuggestManualKeywordsGetApi({ site_id: siteId })
  const { data: popular } = useSuggestPopularKeywordsGetApi({ site_id: siteId, days: 30 })
  const showSaved = useShowSavedToast({ editing: true })

  const { trigger: updS } = useSuggestSettingsUpdateApi({
    onSuccess: () => {
      showSaved('人気キーワード設定')
      mutS()
    }
  })
  const { trigger: createKw, isMutating: creating } = useSuggestManualKeywordCreateApi({
    onSuccess: () => {
      setKw('')
      setUrl('')
      mutM()
    }
  })
  const { trigger: deleteKw } = useSuggestManualKeywordDeleteApi({ onSuccess: () => mutM() })

  const [kw, setKw] = useState('')
  const [url, setUrl] = useState('')

  const on = !!settings?.show_initial_popular
  const add = () => {
    const keyword = kw.trim()
    if (!keyword) return
    createKw({ site_id: siteId, keyword, target_url: url.trim() || undefined, pinned: true })
  }

  return (
    <>
      <Area>
        <Heading>入力前の人気キーワード表示</Heading>
        <AreaContent>
          <SettingRow
            title='検索窓フォーカス時に人気キーワードを表示する'
            sub='入力前（空欄）の状態で、固定ピン＋実検索の人気語を提示します'
          >
            <CheckBox
              type={CheckBoxType.TOGGLE_BUTTON}
              name='show_initial_popular'
              checked={on}
              onChange={(v) => updS({ site_id: siteId, show_initial_popular: v })}
            />
          </SettingRow>
          <Text size={12} color={TextColor.SECONDARY}>
            表示の並びは「① 固定キーワード（下で登録）→ ②
            自動人気キーワード（直近30日の検索数上位）」の順です。件数はデバイス別デザインの「ワード」件数に従います。
          </Text>
        </AreaContent>
      </Area>

      <Area>
        <Heading>① 固定キーワード（常に先頭にピン留め）</Heading>
        <AreaContent>
          <Text size={12} color={TextColor.SECONDARY}>
            キャンペーン語や注力商品などを常に先頭に出せます（誘導先URLは任意）。
          </Text>
          <div className='mt-3 flex flex-wrap items-end gap-2'>
            <div className='flex flex-col gap-1'>
              <Text size={12} color={TextColor.SECONDARY}>
                キーワード
              </Text>
              <input
                className={`${textInput} w-48`}
                value={kw}
                placeholder='例: 福袋'
                onChange={(e) => setKw(e.target.value)}
              />
            </div>
            <div className='flex flex-col gap-1'>
              <Text size={12} color={TextColor.SECONDARY}>
                誘導先URL（任意）
              </Text>
              <input
                className={`${textInput} w-64`}
                value={url}
                placeholder='/campaign/fukubukuro など'
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
            <Button
              color={ButtonColor.PRIMARY}
              onClick={add}
              loading={creating}
              disabled={!kw.trim()}
            >
              追加
            </Button>
          </div>

          <div className='mt-3 flex flex-col gap-2'>
            {(manual ?? []).map((m) => (
              <div
                key={m.id}
                className='border-border-input flex flex-wrap items-center gap-3 rounded-lg border p-3'
              >
                <Text weight='bold'>📌 {m.keyword}</Text>
                {m.target_url && (
                  <Text size={12} color={TextColor.SECONDARY}>
                    → {m.target_url}
                  </Text>
                )}
                <div className='ml-auto'>
                  <Button
                    color={ButtonColor.CRITICAL}
                    onClick={() => deleteKw({ site_id: siteId, keyword_id: m.id })}
                  >
                    削除
                  </Button>
                </div>
              </div>
            ))}
            {manual && manual.length === 0 && (
              <Text color={TextColor.SECONDARY}>固定キーワードはまだありません。</Text>
            )}
          </div>
        </AreaContent>
      </Area>

      <Area>
        <Heading>② 自動人気キーワード（直近30日・読み取り専用）</Heading>
        <AreaContent>
          <Text size={12} color={TextColor.SECONDARY}>
            実際の検索回数が多い語（0件ヒットを除く）。固定キーワードの後ろに自動補完されます。
          </Text>
          <div className='mt-3 flex flex-wrap gap-2'>
            {(popular ?? []).map((p) => (
              <span
                key={p.keyword}
                className='border-border-input bg-fill-surface-tertiary rounded-full border px-3 py-1 text-[12px]'
              >
                {p.keyword}
                <span className='text-secondary ml-1'>({p.count})</span>
              </span>
            ))}
            {popular && popular.length === 0 && (
              <Text color={TextColor.SECONDARY}>
                まだ集計データがありません（実サイトでの検索が蓄積されると表示されます）。
              </Text>
            )}
          </div>
        </AreaContent>
      </Area>
    </>
  )
}

// --- 言語処理・パーソナライズ（別タブ：設定APIのみを保存） ---
const LanguagePersonalizeContent = ({ siteId }: { siteId: number }) => {
  const { data: settings, error, isLoading, mutate } = useSuggestSettingsGetApi({ site_id: siteId })
  const { data: site } = useSiteGetApi({ site_id: siteId })
  const recoOn = !!site?.enable_reco_media

  const showSaved = useShowSavedToast({ editing: true })
  const { trigger, isMutating } = useSuggestSettingsUpdateApi({
    onSuccess: () => {
      showSaved('言語・パーソナライズ設定')
      setDraft({})
      mutate()
    }
  })

  const [draft, setDraft] = useState<Partial<SuggestSettings>>({})
  const sv = <K extends keyof SuggestSettings>(key: K): SuggestSettings[K] =>
    key in draft ? (draft[key] as SuggestSettings[K]) : (settings as SuggestSettings)[key]
  const setS = <K extends keyof SuggestSettings>(key: K, value: SuggestSettings[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))
  const tg = (key: keyof SuggestSettings, title: string, sub?: string) => (
    <SettingRow title={title} sub={sub}>
      <CheckBox
        type={CheckBoxType.TOGGLE_BUTTON}
        name={String(key)}
        checked={!!sv(key)}
        onChange={(v) => setS(key, v as never)}
      />
    </SettingRow>
  )
  const dirty = Object.keys(draft).length > 0
  const save = () => trigger({ site_id: siteId, ...draft })

  return (
    <DetailPageLoader error={error} data={!isLoading && settings}>
      {settings && (
        <>
          <Area>
            <Heading>言語処理（表記ゆれ・補正）</Heading>
            <AreaContent>
              {tg(
                'normalize',
                '表記ゆれを吸収する',
                'ひらがな/カタカナ/漢字の読みを揃えて一致させます'
              )}
              {tg('fuzzy', 'あいまい検索（タイプミス補正）')}
              {sv('fuzzy') && (
                <SettingRow title='補正の信頼度しきい値' sub='50〜99（高いほど厳密）'>
                  <input
                    type='number'
                    min={50}
                    max={99}
                    className={`${textInput} w-24`}
                    value={sv('fuzzy_threshold')}
                    onChange={(e) => setS('fuzzy_threshold', Number(e.target.value))}
                  />
                </SettingRow>
              )}
              <SettingRow title='補正の出し方'>
                <Selector
                  name='correction_mode'
                  choices={[
                    { value: 'suggest', name: 'もしかして提示' },
                    { value: 'auto', name: '自動補正' }
                  ]}
                  value={sv('correction_mode')}
                  onChange={(e) => setS('correction_mode', e.target.value)}
                />
              </SettingRow>
            </AreaContent>
          </Area>

          <Area>
            <Heading>パーソナライズ</Heading>
            <AreaContent>
              {recoOn ? (
                <>
                  {tg('personalize', '閲覧・購入履歴で候補を最適化する')}
                  {tg('use_order_weight', '購入実績を重み付けに使う')}
                  {tg('use_reco', 'AIレコメンドのスコアを併用する')}
                </>
              ) : (
                <Text size={14} color={TextColor.SECONDARY}>
                  パーソナライズはAIレコメンド（RecoMedia）がONのサイトで利用できます。サイト管理でRecoMediaを有効にすると、閲覧・購入履歴を使った候補最適化を設定できます。
                </Text>
              )}
            </AreaContent>
          </Area>

          <Area noBorder>
            <AreaContent>
              <div className='flex items-center gap-3'>
                <Button
                  color={ButtonColor.PRIMARY}
                  onClick={save}
                  loading={isMutating}
                  disabled={!dirty}
                >
                  保存する
                </Button>
                {dirty && (
                  <Text size={12} color={TextColor.SECONDARY}>
                    未保存の変更があります。
                  </Text>
                )}
              </div>
            </AreaContent>
          </Area>
        </>
      )}
    </DetailPageLoader>
  )
}

// --- 統合設定ページ本体 ---
const DESIGN_DEFAULTS: SuggestDeviceDesign = {
  count_word: 5,
  count_product: 4,
  count_category: 3,
  count_brand: 3,
  count_article: 3,
  show_product_name: true,
  show_catchcopy: false,
  catchcopy_length: 30,
  show_outline: false,
  outline_length: 60,
  show_thumb: true,
  show_price: true,
  show_stock: false,
  show_out_of_stock: true,
  show_article_title: true,
  show_article_category: false,
  show_article_thumb: false,
  show_article_topics: false,
  topic_length: 20,
  show_article_created: false,
  show_article_updated: false,
  theme_color: '',
  custom_css: ''
}

const SettingsContent = ({ siteId }: { siteId: number }) => {
  const {
    data: settings,
    error: sErr,
    isLoading: sLoad,
    mutate: mutS
  } = useSuggestSettingsGetApi({ site_id: siteId })
  const {
    data: delivery,
    error: dErr,
    isLoading: dLoad,
    mutate: mutD
  } = useSuggestDeliveryGetApi({ site_id: siteId })

  const showSaved = useShowSavedToast({ editing: true })
  const { trigger: updS } = useSuggestSettingsUpdateApi()
  const { trigger: updD } = useSuggestDeliveryUpdateApi()

  const [pcDraft, setPcDraft] = useState<Partial<SuggestDeviceDesign>>({})
  const [spDraft, setSpDraft] = useState<Partial<SuggestDeviceDesign>>({})
  const [metaDraft, setMetaDraft] = useState<{
    surface_pc?: boolean
    surface_sp?: boolean
  }>({})
  const [saving, setSaving] = useState(false)

  // 配信メタ（配信デバイス）の有効値
  const mv = <K extends 'surface_pc' | 'surface_sp'>(
    key: K,
    def: SuggestDelivery[K]
  ): SuggestDelivery[K] =>
    key in metaDraft ? (metaDraft[key] as SuggestDelivery[K]) : (delivery?.[key] ?? def)
  const setM = (patch: Partial<typeof metaDraft>) => setMetaDraft((d) => ({ ...d, ...patch }))

  // 配信デバイスのON/OFF（OFFのデバイスは件数表・デザイン・プレビューを連動して無効化）
  const surfacePc = mv('surface_pc', true)
  const surfaceSp = mv('surface_sp', true)

  const effPc: SuggestDeviceDesign = { ...(delivery?.pc ?? DESIGN_DEFAULTS), ...pcDraft }
  const effSp: SuggestDeviceDesign = { ...(delivery?.sp ?? DESIGN_DEFAULTS), ...spDraft }

  // 件数・表示項目・テーマ・CSS をデバイス別 draft へ書き込む共通セッター
  const setPcField = (k: keyof SuggestDeviceDesign, v: string | number | boolean) =>
    setPcDraft((d) => ({ ...d, [k]: v as never }))
  const setSpField = (k: keyof SuggestDeviceDesign, v: string | number | boolean) =>
    setSpDraft((d) => ({ ...d, [k]: v as never }))

  const dirty =
    Object.keys(pcDraft).length > 0 ||
    Object.keys(spDraft).length > 0 ||
    Object.keys(metaDraft).length > 0

  const save = async () => {
    if (!settings) return
    setSaving(true)
    try {
      // 種別の表示有無（enable_*）は PC/SP いずれかの件数>0 に同期する
      const enablePatch: Partial<SuggestSettings> = {}
      TYPE_ROWS.forEach((row) => {
        const on = effPc[row.countKey] > 0 || effSp[row.countKey] > 0
        if (settings[row.enableKey] !== on) {
          enablePatch[row.enableKey] = on as never
        }
      })
      if (Object.keys(enablePatch).length > 0) {
        await updS({ site_id: siteId, ...enablePatch })
      }
      const dPatch: {
        site_id: number
        surface_pc?: boolean
        surface_sp?: boolean
        pc?: Partial<SuggestDeviceDesign>
        sp?: Partial<SuggestDeviceDesign>
      } = { site_id: siteId, ...metaDraft }
      let hasD = Object.keys(metaDraft).length > 0
      if (Object.keys(pcDraft).length > 0) {
        dPatch.pc = pcDraft
        hasD = true
      }
      if (Object.keys(spDraft).length > 0) {
        dPatch.sp = spDraft
        hasD = true
      }
      if (hasD) {
        await updD(dPatch)
      }
      showSaved('検索サジェスト設定')
      setPcDraft({})
      setSpDraft({})
      setMetaDraft({})
      mutS()
      mutD()
    } finally {
      setSaving(false)
    }
  }

  return (
    <DetailPageLoader error={sErr || dErr} data={!sLoad && !dLoad && settings && delivery}>
      {settings && delivery && (
        <>
          <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
            <div className='flex flex-col gap-4'>
              <Area>
                <Heading>配信デバイス</Heading>
                <AreaContent>
                  <SettingRow
                    title='配信するデバイス'
                    sub='ONにしたデバイスでサジェストを表示します'
                  >
                    <div className='flex items-center gap-6'>
                      <div className='flex items-center gap-2'>
                        <Text size={14}>💻 PC</Text>
                        <CheckBox
                          type={CheckBoxType.TOGGLE_BUTTON}
                          name='surface_pc'
                          checked={mv('surface_pc', true)}
                          onChange={(v) => setM({ surface_pc: v })}
                        />
                      </div>
                      <div className='flex items-center gap-2'>
                        <Text size={14}>📱 SP</Text>
                        <CheckBox
                          type={CheckBoxType.TOGGLE_BUTTON}
                          name='surface_sp'
                          checked={mv('surface_sp', true)}
                          onChange={(v) => setM({ surface_sp: v })}
                        />
                      </div>
                    </div>
                  </SettingRow>
                </AreaContent>
              </Area>

              <Area>
                <CandidateTypeMatrix
                  pc={effPc}
                  sp={effSp}
                  setPc={setPcField}
                  setSp={setSpField}
                  pcOn={surfacePc}
                  spOn={surfaceSp}
                />
              </Area>
            </div>

            <PreviewArea
              siteId={siteId}
              pc={effPc}
              sp={effSp}
              pcOn={surfacePc}
              spOn={surfaceSp}
              setPc={setPcField}
              setSp={setSpField}
            />
          </div>

          <Area noBorder>
            <AreaContent>
              <div className='flex items-center gap-3'>
                <Button
                  color={ButtonColor.PRIMARY}
                  onClick={save}
                  loading={saving}
                  disabled={!dirty}
                >
                  保存する
                </Button>
                {dirty && (
                  <Text size={12} color={TextColor.SECONDARY}>
                    未保存の変更があります（共通設定・PC/SPデザインをまとめて保存します）。
                  </Text>
                )}
              </div>
            </AreaContent>
          </Area>
        </>
      )}
    </DetailPageLoader>
  )
}

const SearchSuggestSettingsPage = () => {
  const siteId = useSiteId()
  const [tab, setTab] = useState<PageTabKey>('design')

  return (
    <Main type={MainLayoutType.MEDIUM}>
      <PageTitle
        title='検索サジェスト設定'
        description='候補の種別・並び順などの共通設定と、PC/SPで分離したデザイン、タグ発行・公開をまとめて管理します。'
      />
      <Content>
        <Area noBorder>
          <TabList border>
            {PAGE_TABS.map((t) => (
              <Tab key={t.key} selected={tab === t.key} onClick={() => setTab(t.key)}>
                {t.label}
              </Tab>
            ))}
          </TabList>
        </Area>

        {tab === 'design' && <SettingsContent siteId={siteId} />}

        {tab === 'popular' && <PopularKeywordsContent siteId={siteId} />}

        {tab === 'language' && <LanguagePersonalizeContent siteId={siteId} />}

        {tab === 'tag' && <TagPanel siteId={siteId} />}

        {tab === 'publish' && <PublishContent siteId={siteId} />}
      </Content>
    </Main>
  )
}

export default SearchSuggestSettingsPage
